import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import { Order } from '../../product/context/ProductContext.tsx';
import { HOST } from '../../utils/constants.ts';

export interface User {
  id: number | string;
  name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  description: string;
  isAdmin: boolean;
  orders: Order[];
  isVerified: boolean;
  firstPurchase?: boolean;
}
export interface UserInfo {
  user: User;
  token: string;
}

interface UserContextProps {
  userInfo: UserInfo | null;
  setUserInfo: (userInfo: UserInfo) => void;
  logout: () => void;
}

const UserContext = createContext<UserContextProps | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [userInfo, setUserInfo] = useState<UserInfo | null>(() => {
    const savedUserInfo = localStorage.getItem('userInfo');
    const savedToken = localStorage.getItem('token');
    if (!savedUserInfo || !savedToken) return null;
    return { user: JSON.parse(savedUserInfo), token: savedToken };
  });

  useEffect(() => {
    const savedToken = localStorage.getItem('token');
    if (savedToken) {
      try {
        const decodedData = decodeToken(savedToken);
        if (!decodedData || !decodedData.user) {
          console.warn('Invalid token format, clearing storage');
          localStorage.removeItem('token');
          localStorage.removeItem('userInfo');
          return;
        }

        const { user: email } = decodedData;
        fetch(`${HOST}/users/email/${email}`, {
          headers: {
            Authorization: `Bearer ${savedToken}`,
          },
        })
          .then((response) => {
            if (!response.ok) {
              throw new Error('Error al obtener datos del usuario');
            }
            return response.json();
          })
          .then((user) => {
            setUserInfo({ user, token: savedToken });
          })
          .catch((error) => {
            console.error('Error fetching user data:', error);
            // Clear invalid token
            localStorage.removeItem('token');
            localStorage.removeItem('userInfo');
          });
      } catch (error) {
        console.error('Error decoding token:', error);
        // Clear invalid token
        localStorage.removeItem('token');
        localStorage.removeItem('userInfo');
      }
    }
  }, []);

  const updateUserInfo = (info: UserInfo) => {
    setUserInfo(info);
    localStorage.setItem('token', info.token);
  };

  function decodeToken(token: string): any {
    try {
      if (!token || typeof token !== 'string') {
        throw new Error('Invalid token');
      }

      const parts = token.split('.');
      if (parts.length !== 3) {
        throw new Error('Invalid token format');
      }

      const payload = parts[1];
      // Add padding if needed for base64 decoding
      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      const paddedBase64 = base64.padEnd(
        base64.length + ((4 - (base64.length % 4)) % 4),
        '='
      );

      const decodedPayload = atob(paddedBase64);
      return JSON.parse(decodedPayload);
    } catch (error) {
      console.error('Error in decodeToken:', error);
      throw error;
    }
  }

  const logout = () => {
    setUserInfo(null);
    localStorage.clear();
  };

  // Para manejar el cierre de sesion
  // useEffect(() => {
  //   const savedToken = localStorage.getItem('token');

  //   if (savedToken) {
  //     const { exp } = decodeToken(savedToken);

  //     // Calcula el tiempo restante
  //     const currentTime = Math.floor(Date.now() / 1000); // Tiempo actual en segundos
  //     const timeRemaining = exp - currentTime;

  //     console.log('timeRemaining', timeRemaining);
  //     if (timeRemaining <= 0) {
  //       logout();
  //     } else {
  //       // Configura un temporizador para desloguear al usuario al expirar
  //       const timeout = setTimeout(logout, timeRemaining * 1000);

  //       return () => clearTimeout(timeout); // Limpia el temporizador al desmontar
  //     }
  //   }
  // }, []);

  return (
    <UserContext.Provider
      value={{ userInfo, setUserInfo: updateUserInfo, logout }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUserContext = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUserContext must be used within a UserProvider');
  }
  return context;
};
